import type { AutoBuyEntry, ConstantEntry, SpotDefaultConfig } from '../../api/tradingTypes'
import { PRIMARY_USD_ASSET, USD_ASSETS, isUsd, toNumber } from './tradingConstants'

/** Editable copy of one default config — legacy mutated the loaded object in
 * place (`[(ngModel)]`) and PATCHed that same object back. */
export interface DefaultConfigDraft extends SpotDefaultConfig {
  /** Client-only helper legacy also kept on the object (and sent). */
  maxMopCustomValue: number
  bidConstants: ConstantEntry[]
  gridConstants: ConstantEntry[]
  autoBuy: AutoBuyEntry[]
}

/** `refreshData()` normalisation: a positive `maxMopCount` shows as the
 * "Custom value:" option (-100) with the number in the extra input. */
export function toDraft(config: SpotDefaultConfig): DefaultConfigDraft {
  const copy = JSON.parse(JSON.stringify(config)) as SpotDefaultConfig & { maxMopCustomValue?: number }
  const maxMop = toNumber(copy.maxMopCount)
  let maxMopCustomValue = toNumber(copy.maxMopCustomValue, 0)
  let maxMopCount: number | string = copy.maxMopCount
  if (maxMop > 0) {
    maxMopCustomValue = maxMop
    maxMopCount = -100
  }
  return {
    ...copy,
    maxMopCount,
    maxMopCustomValue,
    bidConstants: Array.isArray(copy.bidConstants) ? copy.bidConstants : [],
    gridConstants: Array.isArray(copy.gridConstants) ? copy.gridConstants : [],
    autoBuy: Array.isArray(copy.autoBuy) ? copy.autoBuy : [],
  }
}

/** `save()`: the "Custom value:" option is resolved back to the number; the
 * rest of the object goes as it is (legacy parity — spotbot takes the fields
 * it knows from the PHP POST bag). */
export function toSavePayload(draft: DefaultConfigDraft): DefaultConfigDraft {
  const payload = JSON.parse(JSON.stringify(draft)) as DefaultConfigDraft
  if (toNumber(payload.maxMopCount) === -100) payload.maxMopCount = payload.maxMopCustomValue
  return payload
}

/** `addNewBidConstant`: key `NAME::ASSET` upper-cased; ignored when the name
 * is blank, when the key exists, or — for the primary USD asset — when the
 * bare name already exists (legacy's pre-`::` keys). Returns null when
 * nothing is added. */
export function addBidConstant(
  constants: ConstantEntry[],
  name: string,
  value: number,
  asset: string,
): ConstantEntry[] | null {
  const trimmedName = name.trim().toUpperCase()
  if (!trimmedName) return null
  const trimmedAsset = asset.trim().toUpperCase()
  const key = `${trimmedName}::${trimmedAsset}`
  for (const item of constants) {
    const existing = String(item.key)
    if (existing === key || (trimmedAsset === PRIMARY_USD_ASSET && existing === trimmedName)) return null
  }
  return [...constants, { key, value: `${value ? value : 0}` }]
}

/** `addNewGridConstant`. */
export function addGridConstant(constants: ConstantEntry[], name: string, value: number): ConstantEntry[] | null {
  const key = name.trim().toUpperCase()
  if (!key) return null
  if (constants.some((item) => String(item.key) === key)) return null
  return [...constants, { key, value: `${value ? value : 0}` }]
}

/** `addNewAutoBuy`. */
export function addAutoBuy(autoBuy: AutoBuyEntry[], coin2: string): AutoBuyEntry[] | null {
  if (autoBuy.some((item) => String(item.key) === coin2)) return null
  return [
    ...autoBuy,
    {
      key: coin2,
      mode: 1,
      amount: isUsd(coin2) ? 15 : 0,
      usdAmount: 15,
      asset: USD_ASSETS.includes(coin2) ? coin2 : PRIMARY_USD_ASSET,
    },
  ]
}

/** The asset dropdown of one auto-buy row: the USD assets unless the row's
 * own key is a USD asset, plus the key itself unless it is `*`. */
export function autoBuyAssetOptions(key: string | number): string[] {
  const k = String(key)
  const options = USD_ASSETS.includes(k) ? [] : [...USD_ASSETS]
  if (k !== '*') options.push(k)
  return options
}
